import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Loader2, Copy, Check, Trash2, Link, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import AppHeader from '@/components/AppHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

// Available roles for team members
const ROLES = [
  { value: 'editor', label: 'Editor', description: 'Can edit projects' },
  { value: 'viewer', label: 'Viewer', description: 'Can view projects only' },
  { value: 'design_reviewer', label: 'Design Reviewer', description: 'Can review and approve designs' },
  { value: 'qa', label: 'QA', description: 'Quality assurance testing' }
];

const TeamPage = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [selectedRole, setSelectedRole] = useState('editor');

  useEffect(() => {
    if (currentWorkspace) fetchMembers();
  }, [currentWorkspace]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const wsId = currentWorkspace?.workspace_id || currentWorkspace?._id;
      const response = await axios.get(`${API}/workspaces/${wsId}/members`);
      setMembers(response.data.members || []);
    } catch (error) {
      // Fallback to workspace members if available
      if (currentWorkspace?.members) {
        setMembers(currentWorkspace.members);
      } else {
        setMembers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const wsId = currentWorkspace?.workspace_id || currentWorkspace?._id;
      const response = await axios.post(`${API}/workspaces/invite`, { role: selectedRole }, { withCredentials: true });
      const fullInviteUrl = `${window.location.origin}/join/${response.data.invite_token}`;
      setInviteLink(fullInviteUrl);
      toast.success(`Invite link generated for ${ROLES.find(r => r.value === selectedRole)?.label || selectedRole} role! Valid for 7 days.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invite link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      const wsId = currentWorkspace?.workspace_id || currentWorkspace?._id;
      await axios.delete(`${API}/workspaces/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const isOwner = currentWorkspace?.owner_id === user?.id;

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title="Team" />
      
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">

        {/* Workspace Info */}
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-zinc-900">{currentWorkspace?.name || 'My Workspace'}</h2>
                <p className="text-zinc-500 text-sm">{currentWorkspace?.display_id || 'WS-001'} • {members.length} member{members.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invite Section */}
        {isOwner && (
          <Card className="bg-white border-zinc-200 mb-6">
            <CardHeader>
              <CardTitle className="text-zinc-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-amber-500" />
                Invite Members
              </CardTitle>
              <CardDescription className="text-zinc-500">Share an invite link to add team members to your workspace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                <p className="text-sm text-zinc-600 mb-4">
                  Select a role and generate an invite link. Team members will join with the selected role.
                </p>
                
                {/* Role Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Member Role
                  </label>
                  <Select value={selectedRole} onValueChange={(value) => { setSelectedRole(value); setInviteLink(''); }}>
                    <SelectTrigger className="w-full bg-white" data-testid="role-select">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{role.label}</span>
                            <span className="text-xs text-zinc-500">{role.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {inviteLink ? (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input 
                        value={inviteLink} 
                        readOnly 
                        className="flex-1 bg-white border-zinc-200 text-sm" 
                        data-testid="invite-link-input"
                      />
                      <Button 
                        onClick={handleCopyLink} 
                        variant="outline" 
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="copy-invite-link-btn"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      This link grants <span className="font-medium text-amber-600">{ROLES.find(r => r.value === selectedRole)?.label}</span> access. Expires in 7 days.
                    </p>
                    <Button 
                      onClick={handleGenerateLink} 
                      variant="ghost" 
                      size="sm"
                      disabled={generatingLink}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      {generatingLink ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
                      Generate New Link
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={handleGenerateLink} 
                    disabled={generatingLink}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="generate-invite-link-btn"
                  >
                    {generatingLink ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                    ) : (
                      <><Link className="w-4 h-4 mr-2" />Generate Invite Link</>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Members List */}
        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="text-zinc-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Users className="w-12 h-12 text-zinc-300 mb-3" />
                <p className="text-zinc-600">No team members yet</p>
                <p className="text-zinc-400 text-sm">Invite members to start collaborating</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {members.map((member, index) => (
                  <div key={member.user_id || index} className="flex items-center justify-between p-4 hover:bg-zinc-50">
                    <div className="flex items-center gap-3">
                      {member.picture ? (
                        <img src={member.picture} alt={member.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <span className="text-amber-600 font-medium">{(member.name || member.email || 'U').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div>
                        <p className="text-zinc-900 font-medium">{member.name || 'Unnamed User'}</p>
                        <p className="text-zinc-400 text-sm">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        member.role === 'owner' ? 'bg-amber-100 text-amber-600' : 'bg-zinc-100 text-zinc-600'
                      }`}>{member.role || 'member'}</span>
                      {isOwner && member.role !== 'owner' && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member.user_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
